import { Box, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import type { ApiError } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import biApi from "../../../../../../../../../api/biApi";
import type { AttachedDocument } from "../../../../../../../../../api/types/svTypes";
import DocumentPreview from "../../../../../../../../common/document-preview/DocumentPreview";
import TransactionAttachedDocumentFileStrip from "./TransactionAttachedDocumentFileStrip";
import TransactionAttachmentListItem from "./TransactionAttachmentListItem";

interface Props {
  items: AttachedDocument[];
  selectedIndex: number;
  selected: AttachedDocument | undefined;
  fetchError: ApiError | undefined;
  isFetching: boolean;
  setSelectedIndex: (index: number) => void;
}
const TransactionAttachmentsDialogContent = ({
  items,
  selectedIndex,
  selected,
  fetchError,
  isFetching,
  setSelectedIndex,
}: Props) => {
  if (fetchError) {
    return <DataLoadingFailed title="Failed to fetch attachments" />;
  }

  if (isFetching) {
    return <InlineLoader />;
  }

  return (
    <>
      <Stack sx={{ borderRight: 1, borderColor: "divider", p: 2, width: "368px" }}>
        <ScrollableFlexContainer>
          {items?.map((item, index) => (
            <TransactionAttachmentListItem
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${item.catalogueId}-${index}`}
              item={item}
              active={index === selectedIndex}
              onClick={() => setSelectedIndex(index)}
              disabled={!item.accessible}
            />
          ))}
        </ScrollableFlexContainer>
      </Stack>
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        {selected ? (
          <TransactionAttachedDocumentFileStrip item={selected} />
        ) : (
          <Stack sx={{ alignItems: "center", justifyContent: "center", flexGrow: 1, width: "100%", height: "100%" }}>
            <Typography color="textSecondary">No accessible attachments found</Typography>
          </Stack>
        )}
        <Box sx={{ flex: 1, height: "100%" }}>
          {selected && <AttachedDocumentPreview catalogueId={selected.catalogueId} />}
        </Box>
      </Stack>
    </>
  );
};

const AttachedDocumentPreview = ({ catalogueId }: { catalogueId: string }) => {
  const getFileDownloadInfo = useCallback(() => biApi.getAttachedDocumentDownloadInfo(catalogueId), [catalogueId]);
  return <DocumentPreview getFileDownloadInfoCallback={getFileDownloadInfo} />;
};

export default TransactionAttachmentsDialogContent;
